
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { WhatsAppConnection } from "@/components/settings/WhatsAppConnection";
import { WhatsAppSettings } from "@/components/settings/WhatsAppSettings";
import { TelegramSettings } from "@/components/settings/telegram/TelegramSettings";
import { InstagramSettings } from "@/components/settings/instagram/InstagramSettings";
import { MessageCircle, MessageSquare, Webhook, Instagram } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const Integrations = () => {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);

  // Função para obter e configurar o ID da empresa com melhor persistência
  const getCompanyId = useCallback(async () => {
    try {
      console.log('Integrations: Tentando obter ID da empresa...');
      
      // Primeiro tentar obter do usuário atual
      if (currentUser?.companyId) {
        console.log('Integrations: Obtendo ID da empresa do usuário atual:', currentUser.companyId);
        setCompanyId(currentUser.companyId);
        // Atualizar o localStorage para garantir consistência
        localStorage.setItem('currentCompanyId', currentUser.companyId);
        return currentUser.companyId;
      }
      
      // Verificar sessão atual
      const { data } = await supabase.auth.getSession();
      const session = data?.session;
      
      if (session) {
        console.log('Integrations: Sessão encontrada, buscando dados do usuário:', session.user.email);
        
        // Buscar dados do usuário no banco
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('company_id, id')
          .eq('email', session.user.email)
          .maybeSingle();
          
        if (userError) {
          console.error('Integrations: Erro ao buscar dados do usuário:', userError);
        } else if (userData && userData.company_id) {
          console.log('Integrations: ID da empresa encontrado no banco:', userData.company_id);
          setCompanyId(userData.company_id);
          localStorage.setItem('currentCompanyId', userData.company_id);
          
          // Armazenar com chave específica do usuário
          if (session.user.id) {
            localStorage.setItem(`companyId_${session.user.id}`, userData.company_id);
          }
          
          return userData.company_id;
        }
      }
      
      // Se não conseguiu obter do banco, tentar localStorage
      const savedCompanyId = localStorage.getItem('currentCompanyId');
      if (savedCompanyId) {
        console.log('Integrations: Usando ID da empresa do localStorage:', savedCompanyId);
        setCompanyId(savedCompanyId);
        return savedCompanyId;
      }
      
      console.log('Integrations: Não foi possível obter ID da empresa');
      setCompanyId(undefined);
      return undefined;
    } catch (error) {
      console.error('Integrations: Erro ao obter ID da empresa:', error);
      return undefined;
    }
  }, [currentUser]);

  // Efeito para obter o ID da empresa quando o componente montar ou o usuário mudar
  useEffect(() => {
    console.log("Integrations: Inicializando página, currentUser:", currentUser);
    
    // Definir um timer para verificar a sessão periodicamente
    const checkSessionInterval = setInterval(async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          console.log("Integrations: Sessão perdida, redirecionando para login");
          clearInterval(checkSessionInterval);
          window.location.href = '/login';
        }
      } catch (error) {
        console.error("Integrations: Erro ao verificar sessão:", error);
      }
    }, 30000); // Verificar a cada 30 segundos
    
    // Tentar obter o ID da empresa e definir estado da página
    getCompanyId().then(() => {
      setPageLoading(false);
    });
    
    return () => {
      clearInterval(checkSessionInterval);
    };
  }, [currentUser, getCompanyId]);

  // Efeito para atualizar o ID da empresa se o usuário mudar
  useEffect(() => {
    if (currentUser?.companyId && currentUser.companyId !== companyId) {
      console.log('Integrations: ID da empresa atualizado devido a mudança no usuário:', currentUser.companyId);
      setCompanyId(currentUser.companyId);
      localStorage.setItem('currentCompanyId', currentUser.companyId);
    }
  }, [currentUser, companyId]);

  const handleTrigger = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!webhookUrl) {
      toast({
        title: "Erro",
        description: "Por favor, insira a URL do webhook do Zapier",
        variant: "destructive",
      });
      return;
    }

    const currentCompanyId = companyId || await getCompanyId();
    
    if (!currentCompanyId) {
      console.error("Cannot trigger webhook: No company ID available");
      toast({
        title: "Erro",
        description: "Empresa não identificada. Contate o administrador.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log(`Triggering webhook for company: ${currentCompanyId}`);
      await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors",
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          triggered_from: window.location.origin,
          company_id: currentCompanyId
        }),
      });

      toast({
        title: "Requisição Enviada",
        description: "A requisição foi enviada para o Zapier. Verifique o histórico do seu Zap para confirmar o disparo.",
      });
    } catch (error) {
      console.error("Erro ao disparar webhook:", error);
      toast({
        title: "Erro",
        description: "Falha ao disparar o webhook do Zapier. Verifique a URL e tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando integrações...</p>
        </div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Integrações</h2>
          <p className="text-muted-foreground">
            Gerencie as integrações com serviços externos
          </p>
        </div>

        <Card className="p-6">
          <div className="text-center text-amber-600">
            <h3 className="text-xl font-semibold mb-2">Empresa não identificada</h3>
            <p>Não foi possível identificar a empresa associada ao seu usuário. Por favor, contate o administrador do sistema.</p>
            <Button 
              className="mt-4" 
              variant="outline" 
              onClick={() => getCompanyId().then(() => setPageLoading(false))}
            >
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Integrações</h2>
          <p className="text-muted-foreground">
            Gerencie as integrações com serviços externos para a empresa: {companyId}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-6 w-6 text-primary" />
                <CardTitle>WhatsApp</CardTitle>
              </div>
              <CardDescription>
                Configure a integração com WhatsApp para sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <WhatsAppConnection />
              <WhatsAppSettings />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MessageCircle className="h-6 w-6 text-primary" />
                <CardTitle>Telegram</CardTitle>
              </div>
              <CardDescription>
                Configure a integração com Telegram para sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TelegramSettings />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Instagram className="h-6 w-6 text-primary" />
                <CardTitle>Instagram</CardTitle>
              </div>
              <CardDescription>
                Configure a integração com Instagram para sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InstagramSettings />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Webhook className="h-6 w-6 text-primary" />
                <CardTitle>Zapier</CardTitle>
              </div>
              <CardDescription>
                Configure a integração com Zapier para sua empresa
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTrigger} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="webhook" className="text-sm font-medium">
                    URL do Webhook
                  </label>
                  <Input
                    id="webhook"
                    placeholder="Cole aqui a URL do seu webhook do Zapier"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                  />
                </div>
                <Button type="submit" disabled={isLoading || !companyId}>
                  {isLoading ? "Testando..." : "Testar Webhook"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
  );
};

export default Integrations;
